import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Eye, ShoppingBag, Sparkles, RotateCcw, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { key: 'top', label: 'TOP NOTE', arabic: 'المقدمة', desc: 'The opening impression — the first scent that reaches your senses' },
  { key: 'heart', label: 'HEART NOTE', arabic: 'القلب', desc: 'The soul of the fragrance — the signature that defines you' },
  { key: 'base', label: 'BASE NOTE', arabic: 'القاعدة', desc: 'The lasting memory — the scent that stays long after you leave' },
];

// Note category icons / descriptors for visual interest
const NOTE_DESCRIPTORS = {
  'Rose': 'Floral', 'Taif Rose': 'Floral', 'Lime': 'Citrus', 'Bergamot': 'Citrus',
  'Lemon': 'Citrus', 'Mandarin': 'Citrus', 'Grapefruit': 'Citrus', 'Saffron': 'Spice',
  'Indian Saffron': 'Spice', 'Pink Pepper': 'Spice', 'Cardamom': 'Spice', 'Black Pepper': 'Spice',
  'Peach': 'Fruity', 'Pear': 'Fruity', 'Apple': 'Fruity', 'Green Apple': 'Fruity',
  'Blackcurrant': 'Fruity', 'Lychee': 'Fruity', 'Raspberry': 'Fruity', 'Pineapple': 'Fruity',
  'Black Cherry': 'Fruity', 'Passion Fruit': 'Fruity', 'Amber': 'Oriental', 'Vanilla': 'Gourmand',
  'Musk': 'Musky', 'White Musk': 'Musky', 'Oud': 'Woody', 'Rich Oud': 'Woody',
  'Cedar': 'Woody', 'Sandalwood': 'Woody', 'Vetiver': 'Woody', 'Patchouli': 'Woody',
  'Tonka': 'Gourmand', 'Tonka Bean': 'Gourmand', 'Leather': 'Animalic', 'Ambrette': 'Musky',
  'Ambergris': 'Marine', 'Jasmine': 'Floral', 'Iris': 'Powdery', 'Violet': 'Floral',
  'Peony': 'Floral', 'Lily': 'Floral', 'Lily of the Valley': 'Floral', 'Tuberose': 'Floral',
  'Rose': 'Floral', 'Geranium': 'Floral', 'Lavender': 'Aromatic', 'Rosemary': 'Aromatic',
  'Fresh Rosemary': 'Aromatic', 'Orange Blossom': 'Floral', 'Orange Flower': 'Floral',
  'Magnolia': 'Floral', 'Ylang-Ylang': 'Floral', 'Osmanthus': 'Floral', 'Freesia': 'Floral',
  'Cashmere': 'Warm', 'Cashmere Wood': 'Woody', 'Marine': 'Aquatic', 'Tea': 'Green',
  'Mint': 'Fresh', 'Sage': 'Aromatic', 'Honey': 'Gourmand', 'Caramel': 'Gourmand',
  'Cotton Candy': 'Gourmand', 'Incense': 'Smoky', 'Davana': 'Aromatic', 'Heliotrope': 'Floral',
  'Guaiac Wood': 'Woody', 'Amberwood': 'Woody', 'White Wood': 'Woody', 'White Ash Wood': 'Woody',
  'Pepper': 'Spice', 'Ginger': 'Spice', 'Violet Leaf': 'Green', 'Madagascar Patchouli': 'Woody',
  'Haitian Vetiver': 'Woody', 'Sicilian Bergamot': 'Citrus', 'White Amber': 'Oriental',
};

const getCategoryColor = (cat) => {
  const colors = {
    'Floral': '#e8a0bf', 'Citrus': '#f5c542', 'Spice': '#c77d3a', 'Fruity': '#e8734a',
    'Oriental': '#b87333', 'Gourmand': '#c9956b', 'Musky': '#d4c5a9', 'Woody': '#8b6b4a',
    'Animalic': '#7a5c3e', 'Marine': '#5a8fa8', 'Aquatic': '#5a8fa8', 'Powdery': '#c4a8d1',
    'Aromatic': '#7da87d', 'Green': '#6b9e6b', 'Warm': '#b8941f', 'Fresh': '#82c4a0',
    'Smoky': '#8b8b8b',
  };
  return colors[cat] || '#D4AF37';
};

const FragranceAlchemy = () => {
  const { addToCart } = useCart();
  const [addingToBag, setAddingToBag] = useState(false);
  const [step, setStep] = useState(-1); // -1=intro, 0=top, 1=heart, 2=base, 3=matching, 4=result
  const [selections, setSelections] = useState({ top: '', heart: '', base: '' });
  const [uniqueNotes, setUniqueNotes] = useState({ top: [], heart: [], base: [] });
  const [perfumes, setPerfumes] = useState([]);
  const [matchResult, setMatchResult] = useState(null);
  const [fadeIn, setFadeIn] = useState(false);
  const [fillHeight, setFillHeight] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/products/build-your-perfume`);
        setUniqueNotes(res.data.unique_notes);
        setPerfumes(res.data.perfumes);
      } catch (err) {
        console.error('Failed to fetch build perfume data:', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setFadeIn(false);
    const t = setTimeout(() => setFadeIn(true), 50);
    return () => clearTimeout(t);
  }, [step]);

  const handleNoteSelect = useCallback((noteType, note) => {
    setSelections(prev => ({ ...prev, [noteType]: note }));
    // Auto advance after a brief pause
    setTimeout(() => {
      if (noteType === 'top') setStep(1);
      else if (noteType === 'heart') setStep(2);
      else if (noteType === 'base') {
        setStep(3);
        // Start matching animation
        performMatch(selections.top, selections.heart, note);
      }
    }, 400);
  }, [selections]);

  const performMatch = async (top, heart, base) => {
    // Animate fill
    setFillHeight(0);
    const steps = [25, 50, 75, 100];
    for (let i = 0; i < steps.length; i++) {
      await new Promise(r => setTimeout(r, 600));
      setFillHeight(steps[i]);
    }

    try {
      const res = await axios.post(`${API}/products/match-perfume`, { top, heart, base });
      setMatchResult(res.data);
    } catch {
      // Fallback: find best match locally
      let best = null;
      let bestScore = -1;
      for (const p of perfumes) {
        const notes = p.notes || {};
        let score = 0;
        if ((notes.top || []).map(n => n.toLowerCase()).includes(top.toLowerCase())) score++;
        if ((notes.heart || []).map(n => n.toLowerCase()).includes(heart.toLowerCase())) score++;
        if ((notes.base || []).map(n => n.toLowerCase()).includes(base.toLowerCase())) score++;
        if (score > bestScore) { bestScore = score; best = p; }
      }
      setMatchResult({ match: best, score: bestScore, max_score: 3 });
    }

    await new Promise(r => setTimeout(r, 800));
    setStep(4);
  };

  const resetGame = () => {
    setStep(-1);
    setSelections({ top: '', heart: '', base: '' });
    setMatchResult(null);
    setFillHeight(0);
  };

  const match = matchResult?.match;
  const brandColor = match?.brand === 'gissah' ? '#8a6ec8' : '#D4AF37';
  const liquidColor = match?.brand === 'gissah' ? '#6b4e91' : '#b8941f';

  return (
    <section ref={sectionRef} className="relative py-24 md:py-32 bg-[#060504] overflow-hidden" data-testid="fragrance-alchemy-section">
      {/* Background grain */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
      }} />

      <div className="relative max-w-5xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <p className="font-arabic text-2xl md:text-3xl text-[#D4AF37]/40 mb-1">ابنِ عطرك</p>
          <p className="font-heading text-[10px] tracking-[0.6em] text-[#8B6914]/70 mb-5">THE EXPERIENCE</p>
          <h2 className="font-heading text-3xl md:text-5xl tracking-wide text-white/90">
            BUILD YOUR <span className="text-[#D4AF37]">PERFUME</span>
          </h2>
          <div className="w-16 h-px bg-[#D4AF37]/30 mx-auto mt-5" />
        </div>

        {/* Step Indicator */}
        {step >= 0 && step <= 2 && (
          <div className="flex items-center justify-center gap-2 md:gap-4 mb-10" data-testid="step-indicator">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.key}>
                {i > 0 && <div className="w-6 md:w-12 h-px" style={{ backgroundColor: step >= i ? '#D4AF37' : '#D4AF3720' }} />}
                <button
                  onClick={() => { if (i < step) setStep(i); }}
                  className="flex items-center gap-2 px-3 py-1.5 border transition-all duration-500"
                  style={{
                    borderColor: step >= i ? '#D4AF37' : '#D4AF3718',
                    backgroundColor: step === i ? '#D4AF3710' : 'transparent',
                    cursor: i < step ? 'pointer' : 'default',
                  }}
                >
                  <span className="font-heading text-[9px] tracking-[0.15em]" style={{ color: step >= i ? '#D4AF37' : '#D4AF3740' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="hidden md:inline font-heading text-[9px] tracking-[0.15em]" style={{ color: step >= i ? '#D4AF37' : '#D4AF3730' }}>
                    {s.label}
                  </span>
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* INTRO SCREEN */}
        {step === -1 && (
          <div className={`text-center transition-all duration-700 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-testid="build-perfume-intro">
            <div className="max-w-xl mx-auto mb-12">
              <p className="font-body text-white/50 text-sm md:text-base leading-relaxed mb-8">
                Select your preferred Top, Heart, and Base notes and we will match you
                to the perfect fragrance from our collection of over 50 handcrafted perfumes.
              </p>

              {/* Visual: Three floating note cards */}
              <div className="flex items-center justify-center gap-4 md:gap-8 mb-12">
                {STEPS.map((s, i) => (
                  <div key={s.key} className="text-center group" style={{ animationDelay: `${i * 0.2}s` }}>
                    <div className="w-20 h-20 md:w-24 md:h-24 border border-[#D4AF37]/20 flex flex-col items-center justify-center mb-3 bg-[#0a0908] group-hover:border-[#D4AF37]/40 transition-all duration-500"
                      style={{ animation: `float ${3 + i * 0.5}s ease-in-out infinite` }}>
                      <span className="font-heading text-[9px] tracking-[0.2em] text-[#D4AF37]/60 mb-1">{String(i + 1).padStart(2, '0')}</span>
                      <span className="font-heading text-[10px] tracking-[0.15em] text-white/70">{s.label.split(' ')[0]}</span>
                      <span className="font-arabic text-sm text-[#D4AF37]/30 mt-0.5">{s.arabic}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => setStep(0)}
              className="group relative overflow-hidden border border-[#D4AF37] px-10 py-4 font-heading text-[10px] tracking-[0.25em] text-[#D4AF37] transition-all duration-500 hover:text-[#050505]"
              data-testid="build-perfume-start-btn"
            >
              <span className="relative z-10 flex items-center gap-3">
                <Sparkles size={14} />
                BEGIN YOUR JOURNEY
              </span>
              <div className="absolute inset-0 bg-[#D4AF37] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            </button>
          </div>
        )}

        {/* NOTE SELECTION STEPS (0, 1, 2) */}
        {step >= 0 && step <= 2 && (
          <div className={`transition-all duration-600 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="text-center mb-8">
              <p className="font-arabic text-xl text-[#D4AF37]/40 mb-1">{STEPS[step].arabic}</p>
              <h3 className="font-heading text-xl md:text-2xl tracking-wide text-white/90 mb-2">
                CHOOSE YOUR <span className="text-[#D4AF37]">{STEPS[step].label}</span>
              </h3>
              <p className="font-body text-white/40 text-sm max-w-md mx-auto">{STEPS[step].desc}</p>
            </div>

            {/* Selected notes summary */}
            {step > 0 && (
              <div className="flex items-center justify-center gap-4 mb-6">
                {step > 0 && selections.top && (
                  <div className="px-3 py-1 border border-[#D4AF37]/20 bg-[#D4AF37]/5">
                    <span className="font-heading text-[8px] tracking-wider text-[#D4AF37]/50">TOP </span>
                    <span className="font-heading text-xs text-white/70">{selections.top}</span>
                  </div>
                )}
                {step > 1 && selections.heart && (
                  <div className="px-3 py-1 border border-[#D4AF37]/20 bg-[#D4AF37]/5">
                    <span className="font-heading text-[8px] tracking-wider text-[#D4AF37]/50">HEART </span>
                    <span className="font-heading text-xs text-white/70">{selections.heart}</span>
                  </div>
                )}
              </div>
            )}

            {/* Notes Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 md:gap-3" data-testid={`notes-grid-${STEPS[step].key}`}>
              {uniqueNotes[STEPS[step].key]?.map((note) => {
                const cat = NOTE_DESCRIPTORS[note] || 'Unique';
                const catColor = getCategoryColor(cat);
                const isSelected = selections[STEPS[step].key] === note;
                return (
                  <button
                    key={note}
                    onClick={() => handleNoteSelect(STEPS[step].key, note)}
                    className={`group relative p-3 md:p-4 border text-left transition-all duration-300 ${
                      isSelected
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 scale-[1.02]'
                        : 'border-[#D4AF37]/10 bg-[#0a0908] hover:border-[#D4AF37]/30 hover:bg-[#0f0d0a]'
                    }`}
                    data-testid={`note-btn-${note.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="w-2 h-2 rounded-full mb-2 opacity-60 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: catColor }} />
                    <p className="font-heading text-[10px] md:text-xs text-white/80 leading-tight">{note}</p>
                    <p className="font-heading text-[7px] md:text-[8px] tracking-wider mt-1" style={{ color: catColor, opacity: 0.6 }}>{cat.toUpperCase()}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* MATCHING ANIMATION */}
        {step === 3 && (
          <div className={`text-center transition-all duration-700 ${fadeIn ? 'opacity-100' : 'opacity-0'}`} data-testid="matching-animation">
            <p className="font-heading text-[10px] tracking-[0.4em] text-[#D4AF37]/60 mb-2">ANALYZING YOUR SELECTION</p>
            <h3 className="font-heading text-xl md:text-2xl tracking-wide text-white/80 mb-8">
              FINDING YOUR <span className="text-[#D4AF37]">PERFECT MATCH</span>
            </h3>

            {/* Selected notes */}
            <div className="flex items-center justify-center gap-3 md:gap-6 mb-8">
              {['top', 'heart', 'base'].map((key) => (
                <div key={key} className="px-4 py-2 border border-[#D4AF37]/25 bg-[#D4AF37]/5">
                  <p className="font-heading text-[8px] tracking-wider text-[#D4AF37]/50 mb-0.5">{key.toUpperCase()}</p>
                  <p className="font-heading text-sm text-white/80">{selections[key]}</p>
                </div>
              ))}
            </div>

            {/* Animated Bottle */}
            <div className="flex justify-center mb-6">
              <svg viewBox="0 0 200 360" className="w-32 md:w-40 h-auto" style={{ filter: 'drop-shadow(0 0 30px #D4AF3715)' }}>
                <defs>
                  <linearGradient id="matchBottleGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#1a1510" />
                    <stop offset="30%" stopColor="#2a2218" />
                    <stop offset="70%" stopColor="#2a2218" />
                    <stop offset="100%" stopColor="#1a1510" />
                  </linearGradient>
                  <linearGradient id="matchLiquidGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#b8941f" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#b8941f" stopOpacity="0.9" />
                  </linearGradient>
                  <clipPath id="matchClip">
                    <path d="M60,80 L60,50 Q60,35 75,35 L85,35 L85,15 Q85,5 95,5 L105,5 Q115,5 115,15 L115,35 L125,35 Q140,35 140,50 L140,80 L150,100 Q160,120 160,140 L160,310 Q160,340 130,340 L70,340 Q40,340 40,310 L40,140 Q40,120 50,100 Z" />
                  </clipPath>
                </defs>
                <path d="M60,80 L60,50 Q60,35 75,35 L85,35 L85,15 Q85,5 95,5 L105,5 Q115,5 115,15 L115,35 L125,35 Q140,35 140,50 L140,80 L150,100 Q160,120 160,140 L160,310 Q160,340 130,340 L70,340 Q40,340 40,310 L40,140 Q40,120 50,100 Z"
                  fill="url(#matchBottleGrad)" stroke="#3d3428" strokeWidth="1.5" />
                <g clipPath="url(#matchClip)">
                  <rect x="40" y={340 - (fillHeight / 100) * 280} width="120" height={(fillHeight / 100) * 280}
                    fill="url(#matchLiquidGrad)" style={{ transition: 'y 0.8s ease-in-out, height 0.8s ease-in-out' }} />
                </g>
                <rect x="88" y="2" width="24" height="8" rx="3" fill="#3d3428" stroke="#4a3d2e" strokeWidth="0.5" />
              </svg>
            </div>

            {/* Progress bar */}
            <div className="max-w-xs mx-auto">
              <div className="h-0.5 bg-[#D4AF37]/10 overflow-hidden">
                <div className="h-full bg-[#D4AF37] transition-all duration-700 ease-out" style={{ width: `${fillHeight}%` }} />
              </div>
            </div>
          </div>
        )}

        {/* RESULT */}
        {step === 4 && match && (
          <div className={`transition-all duration-700 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} data-testid="perfume-match-result">
            <div className="text-center mb-8">
              <p className="font-heading text-[10px] tracking-[0.4em] text-[#D4AF37]/60 mb-2">YOUR PERFECT MATCH</p>
              <div className="flex items-center justify-center gap-1 mb-1">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="w-2 h-2 rounded-full transition-colors" style={{
                    backgroundColor: i < Math.floor(matchResult.score) ? '#D4AF37' : '#D4AF3720',
                  }} />
                ))}
                <span className="font-heading text-[9px] text-[#D4AF37]/50 ml-2">
                  {Math.floor(matchResult.score)}/3 NOTES MATCHED
                </span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center max-w-4xl mx-auto">
              {/* Left: Real Product Photo + Visual */}
              <div className="flex flex-col items-center">
                <div className="relative mb-6 w-full flex items-center justify-center">
                  {/* Glow */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-48 h-48 md:w-56 md:h-56 rounded-full" style={{
                      background: `radial-gradient(circle, ${brandColor}30, transparent 70%)`,
                      animation: 'bottleGlowPulse 3s ease-in-out infinite',
                    }} />
                  </div>
                  {/* Real product image with subtle backdrop */}
                  <div
                    className="relative z-10 w-56 sm:w-64 md:w-72 h-72 sm:h-80 md:h-96 flex items-center justify-center border border-[#D4AF37]/20 bg-gradient-to-b from-[#0a0908] to-[#060504] overflow-hidden"
                    style={{ filter: `drop-shadow(0 0 30px ${brandColor}25)` }}
                    data-testid="match-product-image-frame"
                  >
                    {match.image ? (
                      <img
                        src={match.image}
                        alt={match.name}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'flex'); }}
                        className="w-full h-full object-contain p-4"
                        data-testid="match-product-image"
                      />
                    ) : null}
                    {/* Fallback if no image */}
                    <div
                      className="w-full h-full flex flex-col items-center justify-center"
                      style={{ display: match.image ? 'none' : 'flex' }}
                    >
                      <Sparkles size={36} style={{ color: brandColor }} className="opacity-40 mb-3" />
                      <p className="font-heading text-[10px] tracking-[0.3em] text-white/40">QASR</p>
                      <p className="font-heading text-xs text-white/60 mt-1 text-center px-4">{match.name}</p>
                    </div>
                    {/* Subtle gold corner accents */}
                    <span className="absolute top-2 left-2 w-3 h-3 border-l border-t" style={{ borderColor: `${brandColor}80` }} />
                    <span className="absolute top-2 right-2 w-3 h-3 border-r border-t" style={{ borderColor: `${brandColor}80` }} />
                    <span className="absolute bottom-2 left-2 w-3 h-3 border-l border-b" style={{ borderColor: `${brandColor}80` }} />
                    <span className="absolute bottom-2 right-2 w-3 h-3 border-r border-b" style={{ borderColor: `${brandColor}80` }} />
                  </div>
                </div>

                {/* Your selections recap */}
                <div className="flex gap-2 flex-wrap justify-center">
                  {['top', 'heart', 'base'].map((key) => (
                    <div key={key} className="px-2 py-1 border border-[#D4AF37]/15 bg-[#0a0908]">
                      <p className="font-heading text-[7px] tracking-wider text-[#D4AF37]/40">{key.toUpperCase()}</p>
                      <p className="font-heading text-[10px] text-white/60">{selections[key]}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Product Details */}
              <div className="text-center md:text-left">
                <div className="inline-block mb-3">
                  <span className="font-heading text-[9px] tracking-[0.3em] px-3 py-1 border" style={{ color: brandColor, borderColor: brandColor + '40' }}>
                    {match.brand?.toUpperCase()}
                  </span>
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl md:text-4xl text-white/95 tracking-wide mb-2 break-words" data-testid="match-perfume-name">
                  {match.name}
                </h3>
                <div className="flex items-baseline gap-2 mb-4 justify-center md:justify-start flex-wrap">
                  {match.original_price && match.original_price > match.price && (
                    <span className="font-body text-sm line-through" style={{ color: `${brandColor}80` }}>A${match.original_price.toFixed(2)}</span>
                  )}
                  {match.price > 0 && (
                    <p className="font-heading text-lg" style={{ color: brandColor }}>A${match.price.toFixed(2)}</p>
                  )}
                  {match.discount_pct ? (
                    <span className="font-heading text-[10px] tracking-wider px-1.5 py-0.5" style={{ background: '#D4AF37', color: '#050505' }}>{match.discount_pct}% OFF</span>
                  ) : null}
                </div>

                {match.description && (
                  <p className="font-body text-sm text-white/50 leading-relaxed mb-6 line-clamp-3">{match.description}</p>
                )}

                {/* Notes pyramid */}
                <div className="mb-6 space-y-3">
                  {['top', 'heart', 'base'].map((noteType) => {
                    const noteList = match.notes?.[noteType] || [];
                    const labels = { top: 'TOP', heart: 'HEART', base: 'BASE' };
                    return (
                      <div key={noteType} className="flex items-center gap-3 justify-center md:justify-start flex-wrap">
                        <span className="font-heading text-[8px] tracking-[0.2em] text-[#D4AF37]/40 w-10 shrink-0">{labels[noteType]}</span>
                        <div className="flex flex-wrap gap-1.5 justify-center md:justify-start">
                          {noteList.map((n, i) => (
                            <span key={i} className="font-body text-xs text-white/60 px-2 py-0.5 border border-[#D4AF37]/10">{n}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {match.scent_profile?.feels_like && (
                  <div className="p-4 border border-[#D4AF37]/15 bg-[#0a0908] mb-6 text-left">
                    <p className="font-heading text-[8px] tracking-[0.3em] text-[#D4AF37]/40 mb-2">FEELS LIKE</p>
                    <p className="font-body text-sm text-white/50 italic leading-relaxed">{match.scent_profile.feels_like}</p>
                  </div>
                )}

                {/* Action Buttons — stack on mobile, side-by-side ≥sm */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <Link
                    to={`/products/${match.id}`}
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-[#D4AF37] text-[#050505] font-heading text-[10px] tracking-[0.2em] hover:bg-[#E5C158] transition-colors"
                    data-testid="match-view-product-btn"
                  >
                    <Eye size={14} />
                    VIEW PRODUCT
                  </Link>
                  <button
                    onClick={async () => {
                      if (addingToBag || !match?.id) return;
                      setAddingToBag(true);
                      try {
                        await addToCart(match.id, 1);
                      } finally {
                        setAddingToBag(false);
                      }
                    }}
                    disabled={addingToBag || match?.in_stock === false}
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] font-heading text-[10px] tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="match-add-to-bag-btn"
                  >
                    {addingToBag ? (
                      <><Loader2 size={14} className="animate-spin" /> ADDING…</>
                    ) : (
                      <><ShoppingBag size={14} /> ADD TO BAG</>
                    )}
                  </button>
                </div>

                <button
                  onClick={resetGame}
                  className="w-full flex items-center justify-center gap-2 py-2 text-white/25 hover:text-white/50 transition-colors"
                  data-testid="build-perfume-restart-btn"
                >
                  <RotateCcw size={12} />
                  <span className="font-heading text-[9px] tracking-wider">TRY DIFFERENT NOTES</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bottom flourish */}
        <div className="text-center mt-16 md:mt-20">
          <div className="flex items-center justify-center gap-4">
            <div className="w-20 h-px bg-gradient-to-r from-transparent to-[#D4AF37]/20" />
            <span className="text-[#D4AF37]/15 text-xs">&#10023;</span>
            <div className="w-20 h-px bg-gradient-to-l from-transparent to-[#D4AF37]/20" />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bottleGlowPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </section>
  );
};

export default FragranceAlchemy;
